/**
 * runidea.ai — Phase 13 — Multifamily summary card.
 *
 * Renders the `unit_mix_summary` aggregate alongside the normal rental
 * Financials tab so users can see total-units / blended rent at a glance.
 * The actual financial waterfall is the same as a rental deal (effective
 * rent is plugged into the rental engine on the backend).
 */
import { useFmt } from "@/lib/useFmt";
import { Building2 } from "lucide-react";

export default function MultifamilySummaryCard({ deal }) {
  const fmt = useFmt();
  const summary = deal.unit_mix_summary || {};
  const mix = Array.isArray(deal.unit_mix) ? deal.unit_mix : [];
  if (!mix.length && !summary.total_units) return null;
  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6 shadow-sm sm:p-8" data-testid="multifamily-summary-card">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-blue-600" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-700">Multifamily unit mix</h3>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Units</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900" data-testid="mf-total-units">{summary.total_units ?? 0}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Potential rent</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{fmt.money(summary.total_potential_rent)}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Effective rent</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-700">{fmt.money(summary.total_effective_rent)}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Avg rent / unit</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{fmt.money(summary.avg_rent_per_unit)}</p>
        </div>
      </div>

      {mix.length > 0 ? (
        <div className="mt-5 overflow-hidden rounded-xl border border-blue-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-blue-50/50 text-xs uppercase tracking-wider text-blue-700">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Label</th>
                <th className="px-3 py-2 text-right font-medium">Units</th>
                <th className="px-3 py-2 text-right font-medium">Beds / Baths</th>
                <th className="px-3 py-2 text-right font-medium">Rent / mo</th>
                <th className="px-3 py-2 text-right font-medium">Vacancy</th>
                <th className="px-3 py-2 text-right font-medium">Gross</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-50">
              {mix.map((r, i) => (
                <tr key={i} data-testid={`mf-row-${i}`}>
                  <td className="px-3 py-2 text-slate-700">{r.label || `Unit ${i + 1}`}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count ?? 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {r.bedrooms ?? 0} / {r.bathrooms ?? 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt.money(r.rent)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.vacancy_pct ?? 0}%</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    {fmt.money((r.count || 0) * (r.rent || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
